import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { CheckIcon, XIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const getStatus = (qty, low, critical) => {
  if (qty === 0) return { label: "Out", color: "#ef4444" };
  if (qty <= (critical || 5)) return { label: "Critical", color: "#ef4444" };
  if (qty <= (low || 10)) return { label: "Low", color: "#f59e0b" };
  return { label: "In Stock", color: "#34d399" };
};

function RequestPopup({ product, store, onClose, onSubmit }) {
  const [qty, setQty] = useState("");
  const currentUser = (() => { try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; } })();

  const handleSubmit = () => {
    if (!qty || parseInt(qty) <= 0) return;
    onSubmit({ store, productId: product.id, productName: product.name, sku: product.sku, quantityRequested: parseInt(qty), requestedBy: currentUser?.name || "Unknown" });
    onClose();
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 420, background: "#F4F2EC", border: "2px solid #0A0A0F", borderRadius: 0, padding: 24, zIndex: 301 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>Request Restock</h3>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>{product.name} {product.sku ? `(${product.sku})` : ""}</p>
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Quantity Needed</label>
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} min="1" placeholder="e.g. 50" autoFocus
            style={S.input} onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={S.clearBtn}>Cancel</button>
          <button onClick={handleSubmit} style={{ ...S.saveJobBtn, width: "auto", padding: "10px 24px" }}>Submit Request</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function InventoryPage({ store, storeName }) {
  const isVV = true; // base product + pack size enabled for all stores
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingQty, setEditingQty] = useState(null); // product id or "base-<name>"
  const [qtyInput, setQtyInput] = useState("");
  const [requestProduct, setRequestProduct] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedThresholds, setExpandedThresholds] = useState(new Set());
  const [thresholdInputs, setThresholdInputs] = useState({});

  const toggleGroup = (bp) => setExpandedGroups((prev) => { const s = new Set(prev); if (s.has(bp)) s.delete(bp); else s.add(bp); return s; });
  const toggleThreshold = (key) => setExpandedThresholds((prev) => { const s = new Set(prev); if (s.has(key)) s.delete(key); else s.add(key); return s; });

  const fetchProducts = useCallback(() => {
    fetch(`${API_URL}/api/products?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setProducts(data); })
      .catch(() => {});
  }, [store]);

  const fetchRequests = useCallback(() => {
    fetch(`${API_URL}/api/restock?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setRequests(data); })
      .catch(() => {});
  }, [store]);

  useEffect(() => { fetchProducts(); fetchRequests(); }, [fetchProducts, fetchRequests]);

  const filtered = useMemo(() => {
    if (!filter) return products;
    const f = filter.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(f) || (p.sku || "").toLowerCase().includes(f) || (p.baseProduct || "").toLowerCase().includes(f));
  }, [products, filter]);

  // Build base product inventory map: base name → the units held by the ONE row
  // that carries the family's pool. Lowest id wins, which is the row the server
  // reads and writes — two singles can legitimately share a base product, and
  // taking the last one seen would read a different row than the server does.
  // A bundle holds no stock, so it can never be that row.
  const baseHolder = useMemo(() => {
    const map = {};
    [...products].sort((a, b) => a.id - b.id).forEach((p) => {
      if (!p.baseProduct || p.isBundle) return;
      if ((p.packSize || 1) === 1 && !map[p.baseProduct]) map[p.baseProduct] = p;
    });
    return map;
  }, [products]);

  const baseInventory = useMemo(() => {
    const map = {};
    products.forEach((p) => { if (p.baseProduct && !p.isBundle && map[p.baseProduct] === undefined) map[p.baseProduct] = 0; });
    Object.entries(baseHolder).forEach(([bp, p]) => { map[bp] = p.quantity || 0; });
    return map;
  }, [products, baseHolder]);

  // Thresholds live on the same row that carries the pool.
  const baseThresholds = useMemo(() => {
    const map = {};
    Object.entries(baseHolder).forEach(([bp, p]) => {
      map[bp] = { low: p.lowStockThreshold ?? 10, critical: p.criticalStockThreshold ?? 5 };
    });
    return map;
  }, [baseHolder]);

  const saveBaseQty = (baseName) => {
    const baseProduct = baseHolder[baseName];
    if (baseProduct) {
      fetch(`${API_URL}/api/inventory/${store}/${baseProduct.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity: parseInt(qtyInput) || 0 }),
      }).then(() => { fetchProducts(); setEditingQty(null); });
    }
  };

  const saveQty = (pid) => {
    fetch(`${API_URL}/api/inventory/${store}/${pid}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity: parseInt(qtyInput) || 0 }),
    }).then(() => { fetchProducts(); setEditingQty(null); });
  };

  const saveThresholds = (pid, low, critical) => {
    fetch(`${API_URL}/api/inventory/${store}/${pid}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity: products.find((p) => p.id === pid)?.quantity || 0, lowStockThreshold: parseInt(low) || 10, criticalStockThreshold: parseInt(critical) || 5 }),
    }).then(() => { fetchProducts(); setSaveMsg("Thresholds updated"); setTimeout(() => setSaveMsg(""), 3000); });
  };

  const submitRequest = (data) => {
    fetch(`${API_URL}/api/restock`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(() => { fetchRequests(); setSaveMsg("Restock request submitted"); setTimeout(() => setSaveMsg(""), 3000); });
  };

  // A bundle keeps no stock of its own, so its own column is never counted —
  // the units are already counted on the things inside it.
  const totalProducts = products.length;
  const totalStock = products.reduce((s, p) => s + (p.isBundle ? 0 : p.quantity || 0), 0);
  const outOfStock = products.filter((p) => {
    if (p.isBundle) return (p.derivedQuantity || 0) === 0;
    return !p.baseProduct && (p.quantity || 0) === 0;
  }).length;
  const lowStock = products.filter((p) => {
    if (p.isBundle) {
      const d = p.derivedQuantity || 0;
      return d > 0 && d <= (p.lowStockThreshold ?? 10);
    }
    if (p.baseProduct) return false;
    const qty = p.quantity || 0;
    return qty > 0 && qty <= (p.lowStockThreshold ?? 10);
  }).length;
  const pendingRequests = requests.filter((r) => r.status === "pending").length;

  const statusBadge = (qty, low, critical) => {
    const s = getStatus(qty, low, critical);
    return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 0, fontSize: 12, fontWeight: 600, background: `${s.color}20`, color: s.color, border: `1px solid ${s.color}30` }}>{s.label}</span>;
  };

  // Group products for VV. Bundles are pulled out first and never enter the
  // pooling lists — a bundle that somehow carried a base product would
  // otherwise publish a worked-out number as if it were real stock.
  const { groups, standalone, bundles } = useMemo(() => {
    const b = filtered.filter((p) => p.isBundle);
    const rest = filtered.filter((p) => !p.isBundle);
    if (!isVV) return { groups: {}, standalone: rest, bundles: b };
    const g = {};
    const s = [];
    rest.forEach((p) => {
      if (p.baseProduct) {
        if (!g[p.baseProduct]) g[p.baseProduct] = [];
        g[p.baseProduct].push(p);
      } else s.push(p);
    });
    return { groups: g, standalone: s, bundles: b };
  }, [filtered, isVV]);

  const thresholdBar = (key, pid, low, critical) => {
    const inputKey = `${key}`;
    const inputs = thresholdInputs[inputKey] || { low: String(low), critical: String(critical) };
    return (
      <tr key={`thresh-${key}`} style={{ background: "rgba(200,149,108,0.06)" }}>
        <td colSpan={isVV ? 7 : 5} style={{ padding: "8px 14px 8px 36px", borderBottom: "2px solid #0A0A0F" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Thresholds</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#f59e0b" }}>Low:</span>
              <input type="number" min="0" value={inputs.low}
                onChange={(e) => setThresholdInputs((prev) => ({ ...prev, [inputKey]: { ...inputs, low: e.target.value } }))}
                style={{ ...S.cellInput, width: 55, textAlign: "center", fontSize: 13, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, color: "#0A0A0F", padding: "3px 6px" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#ef4444" }}>Critical:</span>
              <input type="number" min="0" value={inputs.critical}
                onChange={(e) => setThresholdInputs((prev) => ({ ...prev, [inputKey]: { ...inputs, critical: e.target.value } }))}
                style={{ ...S.cellInput, width: 55, textAlign: "center", fontSize: 13, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, color: "#0A0A0F", padding: "3px 6px" }} />
            </div>
            <button onClick={() => saveThresholds(pid, inputs.low, inputs.critical)}
              style={{ padding: "3px 12px", borderRadius: 0, border: "1px solid #22c55e", background: "transparent", color: "#22c55e", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Save
            </button>
            <span style={{ fontSize: 11, color: "rgba(10,10,15,0.58)" }}>
              Out = 0 &middot; Critical &le; {inputs.critical || 5} &middot; Low &le; {inputs.low || 10} &middot; In Stock &gt; {inputs.low || 10}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Inventory</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track stock levels and request restocks</p>
      </div>

      {saveMsg && <div style={{ fontSize: 14, color: "#34d399", textAlign: "center", marginBottom: 16 }}>{saveMsg}</div>}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Products</span><span style={S.statValue}>{totalProducts}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Stock</span><span style={S.statValue}>{totalStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Out of Stock</span><span style={{ ...S.statValue, color: outOfStock > 0 ? "#ef4444" : "#34d399" }}>{outOfStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Low Stock</span><span style={{ ...S.statValue, color: lowStock > 0 ? "#f59e0b" : "#34d399" }}>{lowStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Pending Requests</span><span style={{ ...S.statValue, color: pendingRequests > 0 ? "#0033FF" : "#34d399" }}>{pendingRequests}</span></div>
      </div>

      {/* Pending Requests */}
      {requests.filter((r) => r.status === "pending").length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 12 }}>Pending Restock Requests</h3>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={{ ...S.th, width: "30%" }}>Product</th>
                <th style={{ ...S.th, width: "15%" }}>SKU</th>
                <th style={{ ...S.th, width: "15%", textAlign: "center" }}>Qty Requested</th>
                <th style={{ ...S.th, width: "15%" }}>Requested By</th>
                <th style={{ ...S.th, width: "15%" }}>Date</th>
              </tr></thead>
              <tbody>
                {requests.filter((r) => r.status === "pending").map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...S.td, fontWeight: 500, color: "#0A0A0F" }}>{r.productName}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{r.sku || "—"}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "center", color: "#0033FF", fontWeight: 600 }}>{r.quantityRequested}</td>
                    <td style={{ ...S.td, color: "#0A0A0F" }}>{r.requestedBy}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search products..." style={{ ...S.input, marginBottom: 0, maxWidth: 280 }} />
        <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 14 }}>{totalProducts} product{totalProducts !== 1 ? "s" : ""}</span>
      </div>

      {/* Inventory table */}
      <div style={{ ...S.tableWrap, overflowX: "auto" }}>
        <table style={{ ...S.table, minWidth: 650 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: "35%" }}>Product</th>
              <th style={{ ...S.th, width: "12%" }}>SKU</th>
              {isVV && <th style={{ ...S.th, width: "8%", textAlign: "center" }}>Pack</th>}
              <th style={{ ...S.th, width: "12%", textAlign: "center" }}>Units</th>
              {isVV && <th style={{ ...S.th, width: "12%", textAlign: "center" }}>Can Fulfill</th>}
              <th style={{ ...S.th, width: "10%" }}>Status</th>
              <th style={{ ...S.th, width: "11%", textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {/* Grouped base products (VV only) */}
            {isVV && Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([bp, items]) => {
              const isOpen = expandedGroups.has(bp);
              const baseQty = baseInventory[bp] || 0;
              const isEditingBase = editingQty === `base-${bp}`;
              const thresholds = baseThresholds[bp] || { low: 10, critical: 5 };
              const thresholdOpen = expandedThresholds.has(`base-${bp}`);
              const baseVariant = baseHolder[bp];

              // A shelf row holds the pool but is never sold — it earns no line
              // of its own. A family whose only sellable listing remains after
              // that fold collapses to ONE line: the pool count you edit and
              // the packs it sells, side by side (Daniel: "20 and 4, one line").
              const sellable = items.filter((p) => !p.shelfOnly);
              if (sellable.length === 1) {
                const p = sellable[0];
                const packSize = p.packSize || 1;
                const canFulfill = packSize > 0 ? Math.floor(baseQty / packSize) : 0;
                return (
                  <React.Fragment key={`group-${bp}`}>
                    <tr style={{ background: "rgba(200,149,108,0.04)" }}>
                      <td style={{ ...S.td, padding: "10px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 15, fontWeight: 700 }}>{p.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); toggleThreshold(`base-${bp}`); if (!thresholdInputs[`base-${bp}`]) setThresholdInputs((prev) => ({ ...prev, [`base-${bp}`]: { low: String(thresholds.low), critical: String(thresholds.critical) } })); }}
                            title="Set stock thresholds"
                            style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "2px 8px", cursor: "pointer", fontSize: 11, color: thresholdOpen ? "#0033FF" : "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>
                            L:{thresholds.low} C:{thresholds.critical}
                          </button>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "center", color: "#0033FF" }}>{packSize}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {isEditingBase ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                            <input type="number" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} min="0"
                              autoFocus style={{ ...S.cellInput, width: 60, textAlign: "center", fontSize: 14 }}
                              onKeyDown={(e) => { if (e.key === "Enter") saveBaseQty(bp); if (e.key === "Escape") setEditingQty(null); }} />
                            <button onClick={() => saveBaseQty(bp)} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e", padding: "3px 5px" }}><CheckIcon /></button>
                            <button onClick={() => setEditingQty(null)} style={{ ...S.iconBtn, padding: "3px 5px" }}><XIcon /></button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditingQty(`base-${bp}`); setQtyInput(String(baseQty)); }}
                            title="Single pieces on the shelf — click to edit"
                            style={{ background: "none", border: "1px solid #0033FF", borderRadius: 0, padding: "4px 14px", cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#0033FF", minWidth: 50 }}>
                            {baseQty}
                          </button>
                        )}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, textAlign: "center", color: canFulfill === 0 ? "#ef4444" : canFulfill <= (thresholds.critical || 5) ? "#ef4444" : canFulfill <= (thresholds.low || 10) ? "#f59e0b" : "#34d399" }}>
                        {canFulfill}
                      </td>
                      <td style={S.td}>{statusBadge(canFulfill, thresholds.low, thresholds.critical)}</td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        <button onClick={() => setRequestProduct({ id: items[0]?.id, name: bp, sku: p.sku || "" })} style={{
                          padding: "4px 12px", borderRadius: 0, border: "1px solid #0033FF",
                          background: "transparent", color: "#0033FF", fontSize: 12, fontWeight: 600,
                          cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                        }}>Request</button>
                      </td>
                    </tr>
                    {thresholdOpen && baseVariant && thresholdBar(`base-${bp}`, baseVariant.id, thresholds.low, thresholds.critical)}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={`group-${bp}`}>
                  {/* Group header */}
                  <tr style={{ background: "rgba(200,149,108,0.04)", cursor: "pointer" }}>
                    <td style={{ ...S.td, padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }} onClick={() => toggleGroup(bp)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0033FF" strokeWidth="2.5" strokeLinecap="round"
                            style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s" }}>
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                          <span style={{ fontSize: 15, fontWeight: 700, color: "#0033FF" }}>{bp}</span>
                          <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "1px 8px", borderRadius: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                            {sellable.length} variant{sellable.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); toggleThreshold(`base-${bp}`); if (!thresholdInputs[`base-${bp}`]) setThresholdInputs((prev) => ({ ...prev, [`base-${bp}`]: { low: String(thresholds.low), critical: String(thresholds.critical) } })); }}
                          title="Set stock thresholds"
                          style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "2px 8px", cursor: "pointer", fontSize: 11, color: thresholdOpen ? "#0033FF" : "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>
                          L:{thresholds.low} C:{thresholds.critical}
                        </button>
                      </div>
                    </td>
                    <td style={S.td}></td>
                    <td style={S.td}></td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {isEditingBase ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                          <input type="number" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} min="0"
                            autoFocus style={{ ...S.cellInput, width: 60, textAlign: "center", fontSize: 14 }}
                            onKeyDown={(e) => { if (e.key === "Enter") saveBaseQty(bp); if (e.key === "Escape") setEditingQty(null); }} />
                          <button onClick={() => saveBaseQty(bp)} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e", padding: "3px 5px" }}><CheckIcon /></button>
                          <button onClick={() => setEditingQty(null)} style={{ ...S.iconBtn, padding: "3px 5px" }}><XIcon /></button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingQty(`base-${bp}`); setQtyInput(String(baseQty)); }}
                          style={{ background: "none", border: "1px solid #0033FF", borderRadius: 0, padding: "4px 14px", cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#0033FF", minWidth: 50 }}>
                          {baseQty}
                        </button>
                      )}
                    </td>
                    <td style={S.td}></td>
                    <td style={S.td}>{statusBadge(baseQty, thresholds.low, thresholds.critical)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={() => setRequestProduct({ id: items[0]?.id, name: bp, sku: "" })} style={{
                        padding: "4px 12px", borderRadius: 0, border: "1px solid #0033FF",
                        background: "transparent", color: "#0033FF", fontSize: 12, fontWeight: 600,
                        cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      }}>Request</button>
                    </td>
                  </tr>
                  {/* Threshold editor bar */}
                  {thresholdOpen && baseVariant && thresholdBar(`base-${bp}`, baseVariant.id, thresholds.low, thresholds.critical)}
                  {/* Expanded variants */}
                  {isOpen && sellable.sort((a, b) => (a.packSize || 1) - (b.packSize || 1)).map((p) => {
                    const packSize = p.packSize || 1;
                    const canFulfill = packSize > 0 ? Math.floor(baseQty / packSize) : 0;
                    return (
                      <tr key={p.id} style={{ background: "#FFFFFF" }}>
                        <td style={{ ...S.td, paddingLeft: 36, color: "#0A0A0F" }}>
                          <span style={{ color: "#0A0A0F", marginRight: 6 }}>└</span>{p.name}
                        </td>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "center", color: packSize > 1 ? "#0033FF" : "#0A0A0F" }}>{packSize}</td>
                        <td style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", fontSize: 13 }}>—</td>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, textAlign: "center", color: canFulfill === 0 ? "#ef4444" : canFulfill <= (thresholds.critical || 5) ? "#ef4444" : canFulfill <= (thresholds.low || 10) ? "#f59e0b" : "#34d399" }}>
                          {canFulfill}
                        </td>
                        <td style={S.td}>{statusBadge(canFulfill, thresholds.low, thresholds.critical)}</td>
                        <td style={S.td}></td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {/* Bundles — the count is worked out, so there is nothing to type */}
            {bundles.map((p) => {
              const available = p.derivedQuantity || 0;
              const low = p.lowStockThreshold ?? 10;
              const critical = p.criticalStockThreshold ?? 5;
              const isOpen = expandedGroups.has(`bundle-${p.id}`);
              const members = p.components || [];
              return (
                <React.Fragment key={`bundle-${p.id}`}>
                  <tr style={{ background: "rgba(0,51,255,0.04)" }}>
                    <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => toggleGroup(`bundle-${p.id}`)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0033FF" strokeWidth="2.5" strokeLinecap="round"
                          style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s", flexShrink: 0 }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                        <span>{p.name}</span>
                        <span style={{ ...S.typeBadge, borderColor: "#0033FF", color: "#0033FF" }}>Bundle</span>
                      </div>
                      {p.bundleLimitingMember && (
                        <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 2, paddingLeft: 22 }}>
                          Held back by: {p.bundleLimitingMember.name}
                        </div>
                      )}
                    </td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                    {isVV && <td style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.35)" }}>—</td>}
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <span
                        title="Worked out from what is in it. Change the parts, not this number."
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#0A0A0F", marginRight: 8 }}
                      >
                        {available}
                      </span>
                      <span style={S.typeBadge}>Worked out</span>
                    </td>
                    {isVV && (
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, textAlign: "center", color: available === 0 ? "#ef4444" : available <= critical ? "#ef4444" : available <= low ? "#f59e0b" : "#34d399" }}>
                        {available}
                      </td>
                    )}
                    <td style={S.td}>{statusBadge(available, low, critical)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {/* No Request button here. A restock request for a bundle can
                          never be approved (the server locks a bundle's count), so
                          it would sit pending forever. Request the parts instead. */}
                      <span title="A bundle holds no stock of its own. Request its parts." style={{ color: "rgba(10,10,15,0.35)", fontSize: 12 }}>—</span>
                    </td>
                  </tr>
                  {isOpen && members.map((m) => {
                    const limits = !!p.bundleLimitingMember && p.bundleLimitingMember.id === m.targetId;
                    return (
                      <tr key={`bm-${p.id}-${m.id}`} style={{ background: "#FFFFFF" }}>
                        <td style={{ ...S.td, paddingLeft: 36, color: "#0A0A0F" }}>
                          <span style={{ color: "#0A0A0F", marginRight: 6 }}>└</span>{m.name}
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0033FF", marginLeft: 8 }}>x{m.qty}</span>
                        </td>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{m.sku || "—"}</td>
                        {isVV && <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "center", color: (m.packSize || 1) > 1 ? "#0033FF" : "#0A0A0F" }}>{m.packSize || 1}</td>}
                        <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{m.pool}</td>
                        {isVV && (
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, textAlign: "center", color: limits ? "#ef4444" : "rgba(10,10,15,0.45)" }}>
                            {m.makes}
                          </td>
                        )}
                        <td style={S.td}>
                          {limits && <span style={{ ...S.typeBadge, borderColor: "#ef4444", color: "#ef4444" }}>Holds it back</span>}
                        </td>
                        <td style={S.td}></td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {/* Standalone products */}
            {standalone.map((p) => {
              const qty = p.quantity || 0;
              const isEditing = editingQty === p.id;
              const low = p.lowStockThreshold ?? 10;
              const critical = p.criticalStockThreshold ?? 5;
              const thresholdOpen = expandedThresholds.has(`prod-${p.id}`);
              return (
                <React.Fragment key={p.id}>
                  <tr>
                    <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1 }}>{p.name}</span>
                        <button onClick={() => { toggleThreshold(`prod-${p.id}`); if (!thresholdInputs[`prod-${p.id}`]) setThresholdInputs((prev) => ({ ...prev, [`prod-${p.id}`]: { low: String(low), critical: String(critical) } })); }}
                          title="Set stock thresholds"
                          style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "2px 8px", cursor: "pointer", fontSize: 11, color: thresholdOpen ? "#0033FF" : "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>
                          L:{low} C:{critical}
                        </button>
                      </div>
                    </td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                    {isVV && <td style={{ ...S.td, textAlign: "center", color: "#0A0A0F" }}>1</td>}
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                          <input type="number" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} min="0"
                            autoFocus style={{ ...S.cellInput, width: 60, textAlign: "center", fontSize: 14 }}
                            onKeyDown={(e) => { if (e.key === "Enter") saveQty(p.id); if (e.key === "Escape") setEditingQty(null); }} />
                          <button onClick={() => saveQty(p.id)} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e", padding: "3px 5px" }}><CheckIcon /></button>
                          <button onClick={() => setEditingQty(null)} style={{ ...S.iconBtn, padding: "3px 5px" }}><XIcon /></button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingQty(p.id); setQtyInput(String(qty)); }}
                          style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "4px 14px", cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: "#0A0A0F", minWidth: 50 }}>
                          {qty}
                        </button>
                      )}
                    </td>
                    {isVV && <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, textAlign: "center", color: "#0A0A0F" }}>{qty}</td>}
                    <td style={S.td}>{statusBadge(qty, low, critical)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={() => setRequestProduct(p)} style={{
                        padding: "4px 12px", borderRadius: 0, border: "1px solid #0033FF",
                        background: "transparent", color: "#0033FF", fontSize: 12, fontWeight: 600,
                        cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                      }}>Request</button>
                    </td>
                  </tr>
                  {thresholdOpen && thresholdBar(`prod-${p.id}`, p.id, low, critical)}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={isVV ? 7 : 5} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>
                {filter ? "No products match." : "No products yet. Add products in the Products page first."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {requestProduct && (
        <RequestPopup product={requestProduct} store={store} onClose={() => setRequestProduct(null)} onSubmit={submitRequest} />
      )}
    </div>
  );
}
